import { useEffect } from "react";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { ArticleJsonLd } from "next-seo";
import { Layout } from "@components/Layout/index";
import { getImages } from "@lib/getImages";
import {
  getAllDocumentsByType,
  getEditorial,
  getModalSettings,
  getGlobalConfig,
} from "@lib/api/calls";
import { trackEvent } from "@lib/track-event";
import { Render } from "@components/sections";
import * as redisService from "@services/redis";

export default function Editorial({
  preview,
  title,
  seo,
  editorial,
  slug,
  config,
  subscribed,
  modal,
  allProducts,
}) {
  useEffect(() => {
    trackEvent("view_page", {
      pageTitle: title,
      pageCategory: "Editorial Page",
      pageLocation: window.location.href,
      pagePath: window.location.pathname,
    });
  }, []);
  let images = {};

  if (editorial && editorial.card) {
    images = getImages(editorial.card.cardImage, { small: "600", medium: "600", large: "800" });
  }

  return (
    <Layout
      config={config}
      title={title}
      seo={seo}
      subscribed={subscribed}
      isPreview={preview}
      pageFound={slug !== null}
      modal={modal}
    >
      <main>
        {editorial && editorial.blocks && (
          <>
            {editorial.card && (
              <ArticleJsonLd
                url={`https://kotn.com/editorials/${editorial.slug.current}`}
                title={editorial.title}
                images={[images.small, images.medium, images.large]}
                datePublished={editorial._createdAt}
                dateModified={editorial._updatedAt}
                authorName={[editorial.card.author]}
                description={editorial.card.subheading}
              />
            )}
            <Render sections={editorial.blocks} allProducts={allProducts} />
          </>
        )}
      </main>
    </Layout>
  );
}

export async function getStaticPaths() {
  // Return a list of possible value for id
  const allEditorials = await getAllDocumentsByType("editorial");
  const paths = allEditorials?.map((editorial) => ({
    params: {
      slug: editorial?.slug ? editorial?.slug : 'game-on',
    },
  }));
  return {
    paths,
    fallback: true,
  };
}

export async function getStaticProps({ params, preview = false, locale }) {
  if (params.slug === undefined || params.slug === null ) {
    return;
  }
  const editorialPage = params?.slug ? await getEditorial(params.slug, locale, preview) : null;
  const modal = await getModalSettings(locale, preview);
  const config = await getGlobalConfig(locale, preview);

  let allProducts = [];
  try {
    allProducts = await redisService.getProductList(editorialPage?.blocks);
  } catch (error) {
    allProducts = [];
    console.error(error.stack || error);
  }

  const seo = {
    seoTitle: editorialPage?.meta?.seoTitle || `Kotn · ${editorialPage?.title}`,
    seoDescription: editorialPage?.meta?.seoDescription || "",
    openGraphImage: editorialPage?.meta?.openGraphImage || null,
    twitterCard: editorialPage?.meta?.seoTwitterCardStyle || "summary_large_image",
  };

  return {
    props: {
      preview,
      title: editorialPage?.title || null,
      seo,
      editorial: editorialPage,
      slug: editorialPage?.slug || null,
      modal,
      config,
      allProducts: allProducts,
      ...(await serverSideTranslations(locale, [
        "common",
        "navbar",
        "footer",
        "cart",
        "productcard",
        "recommendations",
      ])),
    },
    revalidate: 60,
  };
}
